import { saveAs } from 'file-saver'
import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

// UI
import { Button, Flex, Heading, Spacer } from '@chakra-ui/react'

// Components

// Hooks
import useOfferList from '@/features/offers/hooks/useOfferList'

// Constants

// Helpers
import { createExcelBlob, createExcelFromAoA } from '@/utils/fileHelper'

// Slices
import { showCreateOfferModal } from '@/stores/offers/offersSlice'
import { getMyOfferTableColumns } from './myOfferTableColumns'
import {
  MyOfferDisplayLevel,
  MySourcingOfferDisplayLevel,
} from '@/features/tableProfiles/constants/tableProfile.constant'
import { camelToWords } from '@/utils/stringHelper'
import TableProfileMenu from '@/components/TableProfileMenu'
import moment from 'moment'

// Components
const MyOfferPageHeader = ({
  count = 0,
  query = {},
  tableState = {},
  setTableState,
  userTableProfile,
  URLSelectedProfileId,
  displayLevel,
  setTableDisplayLevel,
}) => {
  const { useFetchMyOffers } = useOfferList()
  const dispatch = useDispatch()

  const { data: allOffersResponse, isLoading } = useFetchMyOffers({ ...query, page: 1, pageSize: 0 }, displayLevel)

  const handleDownloadOffer = useCallback(() => {
    try {
      if (!tableState?.columnVisibility) {
        toast.error('Column visibility is not set. Please configure the table first.')
        return
      }
      if (!Array.isArray(tableState?.columnOrder)) {
        toast.error('Column visibility is not set. Please configure the table first.')
        return
      }

      const headerKeys = []
      Object.entries(tableState.columnVisibility).forEach(([key, value]) => {
        if (key === 'action') return
        if (value) {
          headerKeys.push(key)
        }
      })
      const labelKeys = [...headerKeys].map((key) =>
        camelToWords(key.replace(new RegExp('commercialCheckList|legal', 'g'), ''))
      )

      const dateKeys = [
        'createdAt',
        'updatedAt',
        'projectStartDate',
        'projectEndDate',
        'offerSubmissionDate',
        'quotationSubmittedDate',
        'quotationCompletedDate',
        'bookingCompletedDate',
        'legalProjectCompletionDate',
        'bookingCreateDate',
        'bookingStartDate',
        'bookingEndDate',
        'bookingSubmittedDate',
        'bookingCustomerPODate',
        'damexCreateDate',
        'damexConfirmDate',
        'sourcingDueDate',
        'costBudgetRfqValidDate',
        'costBudgetRfqSubmitDate',
      ]

      const rowData = allOffersResponse?.data?.map((data) => {
        const row = []
        headerKeys.forEach((key) => {
          if (data[key] !== undefined) {
            if (dateKeys.includes(key)) {
              const date = moment(data[key]).format('DD-MMM-YYYY')
              row.push(date)
            } else if (key.includes('Percentage')) {
              row.push(Number(data[key]) / 100)
            } else {
              row.push(data[key])
            }
          } else {
            row.push('')
          }
        })
        return row
      })

      const excelBlob = createExcelFromAoA([labelKeys, ...rowData])

      saveAs(excelBlob, 'My Offers.xlsx')
    } catch (err) {
      console.error(err)
      toast.error('Failed to download offers.')
    }
  }, [allOffersResponse?.data, tableState])

  const handleCreate = useCallback(() => {
    dispatch(showCreateOfferModal({}))
  }, [dispatch])

  return (
    <Flex py="5" borderBottom="1px solid #d8dbe0" alignItems={'center'} gap="3">
      <Heading mr="5">My Offer ({count})</Heading>
      <TableProfileMenu
        userTableProfile={userTableProfile}
        tableState={tableState}
        setTableState={setTableState}
        tableDisplayLevel={displayLevel}
        setTableDisplayLevel={setTableDisplayLevel}
        URLSelectedProfileId={URLSelectedProfileId}
        allDisplayLevels={[
          {
            value: MyOfferDisplayLevel.OFFER,
            label: `Display Every Offer`,
          },
          {
            value: MyOfferDisplayLevel.SUB_SYSTEM,
            label: `Display Every Sub System`,
          },
          {
            value: MyOfferDisplayLevel.COST_BUDGET,
            label: `Display Every Cost Budget`,
          },
          {
            value: MyOfferDisplayLevel.COST_BUDGET_RFQ,
            label: `Display Every Cost Budget - Vendor RFQ`,
          },
          {
            value: MyOfferDisplayLevel.GCPR,
            label: `Display Every GCPR`,
          },
          {
            value: MyOfferDisplayLevel.GCPR_LINE_ITEM,
            label: `Display Every GCPR - Line Item`,
          },
          {
            value: MyOfferDisplayLevel.PR,
            label: `Display Every PR`,
          },
          {
            value: MyOfferDisplayLevel.PR_LINE_ITEM,
            label: `Display Every PR - Line Item`,
          },
        ]}
      />
      <Flex gap="2">
        <Button colorScheme="gray" onClick={handleDownloadOffer} isLoading={isLoading} loadingText="Downloading">
          Download
        </Button>
        <Button colorScheme="blue" onClick={handleCreate}>
          Create Offer
        </Button>
      </Flex>
    </Flex>
  )
}

export default MyOfferPageHeader
